import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import {
  getAccountingAccess,
  getAccountingChecklist,
  getAccountingProfile,
} from "../../api/accounting";
import { useAuth } from "../../context/AuthContext";
import SalonBusinessCpaAccessRequired from "./SalonBusinessCpaAccessRequired";
import { AccountingProfileForm } from "./SalonBusinessCpaProfileIntake";
import SalonBusinessCpaShell, { AccountingCard } from "./SalonBusinessCpaShell";

const cards = [
  {
    title: "Documents to upload",
    body: "Keep tax slips, bank statements, payroll records, and receipts organized in one place.",
    to: "/salonbusinesscpa/documents",
  },
  {
    title: "GST/HST checklist",
    body: "Track sales tax setup, filing dates, and the records your accountant needs.",
    to: "/salonbusinesscpa/gst-hst",
    moduleKey: "needs_gst_hst",
  },
  {
    title: "Payroll checklist",
    body: "Prepare payroll remittance details, pay stub records, and staff payment notes.",
    to: "/salonbusinesscpa/payroll",
    moduleKey: "needs_payroll",
  },
  {
    title: "Bookkeeping checklist",
    body: "Review monthly sales, expenses, payment deposits, and cleanup items.",
    to: "/salonbusinesscpa/bookkeeping",
    moduleKey: "needs_bookkeeping",
  },
  {
    title: "Corporate tax checklist",
    body: "Organize year-end company records, shareholder notes, and filing details.",
    to: "/salonbusinesscpa/corporate-tax",
    moduleKey: "needs_corporate_tax",
  },
  {
    title: "Personal tax checklist",
    body: "Gather owner and independent professional records for personal tax filing.",
    to: "/salonbusinesscpa/personal-tax",
    moduleKey: "needs_personal_tax",
  },
  {
    title: "Financial statements",
    body: "Prepare clean records for income statements, balance sheets, and owner review.",
    to: "/salonbusinesscpa/financial-statements",
    moduleKey: "needs_financial_statements",
  },
  {
    title: "Messages from accountant",
    body: "Accountant messaging will appear here as the portal grows.",
    to: "/salonbusinesscpa/documents",
  },
];

function statusLabel(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return "Checking";
  if (normalized === "test_free") return "Testing account";
  if (normalized === "no_card_on_file") return "No card on file";
  return normalized
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export default function SalonBusinessCpaDashboard() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [access, setAccess] = useState(null);
  const [profile, setProfile] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let active = true;
    setLoading(true);
    setError("");
    getAccountingAccess(token)
      .then(async (accessData) => {
        if (!active) return;
        setAccess(accessData);
        if (!accessData?.eligible) return;
        const [profileData, checklistData] = await Promise.all([
          getAccountingProfile(token).catch(() => null),
          getAccountingChecklist(token).catch(() => null),
        ]);
        if (!active) return;
        setProfile(profileData);
        setChecklist(checklistData);
      })
      .catch((err) => {
        if (active) {
          setError(
            err?.response?.data?.detail || "Unable to load SalonBusinessCPA access."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token]);

  const checklistCount = useMemo(() => {
    const sections = Array.isArray(checklist?.sections) ? checklist.sections : [];
    return sections.reduce(
      (total, section) => total + (Array.isArray(section.items) ? section.items.length : 0),
      0
    );
  }, [checklist]);

  const visibleCards = useMemo(() => {
    const flags = profile?.module_flags || {};
    if (!profile?.onboarding_completed) {
      return cards.filter((card) => !card.moduleKey);
    }
    return cards.filter((card) => !card.moduleKey || Boolean(flags[card.moduleKey]));
  }, [profile]);

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#f8f6f4] text-sm text-slate-500">
        Loading SalonBusinessCPA...
      </main>
    );
  }

  if (error) {
    return (
      <SalonBusinessCpaShell
        title="SalonBusinessCPA"
        description="We could not load your accounting portal access right now."
      >
        <div className="rounded-3xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
          {error}
        </div>
      </SalonBusinessCpaShell>
    );
  }

  if (!access?.eligible) {
    return <SalonBusinessCpaAccessRequired access={access} />;
  }

  return (
    <SalonBusinessCpaShell
      title="SalonBusinessCPA Dashboard"
      description="Organize accounting, tax, payroll, bookkeeping, GST/HST, and year-end items for your salon."
      actions={
        <>
          <Link
            to="/salonbusinesscpa/documents"
            className="rounded-full bg-rose-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-rose-600"
          >
            Upload documents
          </Link>
          <Link
            to="/salonbusinesscpa/profile"
            className="rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Update profile
          </Link>
        </>
      }
    >
      {!profile?.onboarding_completed ? (
        <section className="rounded-3xl border border-amber-200 bg-amber-50 p-5">
          <h2 className="text-lg font-semibold text-slate-950">
            Start with your accounting profile
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-700">
            Choose the profile that best matches your work so SalonBusinessCPA can
            show the right personal tax, GST/HST, payroll, bookkeeping, and
            corporate tax steps.
          </p>
          <div className="mt-5">
            <AccountingProfileForm
              profile={profile}
              onSaved={async (savedProfile) => {
                setProfile(savedProfile);
                const checklistData = await getAccountingChecklist(token).catch(() => null);
                setChecklist(checklistData);
              }}
            />
          </div>
        </section>
      ) : null}

      <section className="grid gap-4 md:grid-cols-3">
        <AccountingCard title="Access" body="Your portal is connected to your Ellyanna Booking subscription.">
          <p className="text-2xl font-semibold text-slate-950">
            {statusLabel(access.subscription_status)}
          </p>
        </AccountingCard>
        <AccountingCard title="Business" body="Current business context for accounting support.">
          <p className="text-2xl font-semibold text-slate-950">
            {profile?.business_name || user?.business_name || "Your salon"}
          </p>
        </AccountingCard>
        <AccountingCard title="Profile" body="The portal uses this to show the right accounting steps.">
          <p className="text-2xl font-semibold text-slate-950">
            {profile?.client_profile_label || "Not set"}
          </p>
        </AccountingCard>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        <AccountingCard title="Checklist items" body="Tasks prepared for your current profile.">
          <p className="text-2xl font-semibold text-slate-950">{checklistCount || 0}</p>
        </AccountingCard>
        <AccountingCard title="Selected modules" body="Services and situations currently turned on.">
          <p className="text-2xl font-semibold text-slate-950">
            {Object.values(profile?.module_flags || {}).filter(Boolean).length || 0}
          </p>
        </AccountingCard>
        <AccountingCard title="Next best step" body="Complete your profile, then start with documents.">
          <p className="text-2xl font-semibold text-slate-950">
            {profile?.onboarding_completed ? "Documents" : "Profile"}
          </p>
        </AccountingCard>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {visibleCards.map((card) => (
          <AccountingCard key={card.title} {...card} />
        ))}
      </section>
    </SalonBusinessCpaShell>
  );
}
